import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CallDirection, SAPContact } from '../types';
import {
  generateCallReferenceId,
  openSapContactViaCTI,
  SapContact,
} from '../services/sap-cti.service';
import { logger } from '../utils/logger';
import { getSapContactDisplayName } from '../components/call/sap-contact.types';

interface UseSapContactActionsOptions {
  callerNumber?: string | null;
  linkedSapContactId?: string | null;
  callDirection?: CallDirection | null;
  preferredReferenceId?: string | null;
  onContactOpened?: (contact: SapContact) => void;
}

export function useSapContactActions({
  callerNumber,
  linkedSapContactId = null,
  callDirection = null,
  preferredReferenceId = null,
  onContactOpened,
}: UseSapContactActionsOptions) {
  const { t } = useTranslation();

  const handleLinkContact = useCallback(
    (contact: SAPContact) => {
      if (linkedSapContactId === contact.id) {
        return;
      }

      const phone = contact.phone || callerNumber;
      if (!phone) {
        logger.warn('[CallCard] No phone number available to link contact:', contact.id);
        return;
      }

      const referenceId = preferredReferenceId || generateCallReferenceId();
      openSapContactViaCTI(
        contact.id,
        contact.displayId,
        referenceId,
        phone,
        contact.type,
        callDirection,
        contact.firstName
      );

      onContactOpened?.({
        id: contact.id,
        displayId: contact.displayId,
        firstName: contact.firstName,
        name: getSapContactDisplayName(contact, t),
        phone,
        referenceId,
        type: contact.type,
        accountId: contact.accountId,
        startedAt: new Date().toISOString(),
      });
    },
    [linkedSapContactId, callerNumber, callDirection, preferredReferenceId, onContactOpened, t]
  );

  return {
    handleLinkContact,
  };
}
